import { useState, type FormEvent, type ReactNode } from 'react';
import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Burger,
  Button,
  Divider,
  Group,
  Menu,
  NavLink as MantineNavLink,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { IconChevronDown, IconLogout, IconMoon, IconPlus, IconSearch, IconSun } from '@tabler/icons-react';
import { useAuth } from '../auth/AuthContext';
import { navSectionsForTenant, UTILITY_ITEMS, type NavSection } from '../lib/nav';
import { HOME_TASKS } from '../lib/taskButtons';
import { useTenantFeatures } from '../lib/tenantFeatures';
import { HelpButton } from '../features/help/HelpButton';

/** Prefix-match active state (audit fix 2026-07-07, unchanged): every screen
 * group has child routes ('/purchasing-intake/receipts', …) and a bare `===`
 * killed the section highlight the moment one was entered. '/' stays exact (it
 * is a prefix of everything); the '/' separator guard keeps '/settings' from
 * claiming a hypothetical '/settingsomething' (same rule as helpMap.ts's
 * isPrefixMatch). Now shared by sections, their children AND the pinned
 * utility items (Unleashed-parity chrome, Stuart ruling 2026-08-24) — a child
 * tab highlights on the exact same rule as a childless section. */
function isNavActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

/** One main-nav row. Childless sections (Home, Reports, Stores & Transfers)
 * render as a plain link, same as before this goal. A section with `children`
 * (its screen-group layout route's own tab bar, mirrored — see lib/nav.ts)
 * renders as a collapsible NavLink: Mantine shows the chevron automatically
 * once `children` is passed. */
function NavSectionLink({
  section,
  pathname,
  onNavigate,
}: {
  section: NavSection;
  pathname: string;
  onNavigate: () => void;
}) {
  const active = isNavActive(pathname, section.to);

  if (!section.children || section.children.length === 0) {
    return (
      <MantineNavLink
        component={Link}
        to={section.to}
        label={section.label}
        leftSection={<section.icon size={18} stroke={1.5} />}
        active={active}
        onClick={onNavigate}
        className="zs-nav-link"
        mb={2}
      />
    );
  }

  return (
    <MantineNavLink
      component={Link}
      to={section.to}
      label={section.label}
      leftSection={<section.icon size={18} stroke={1.5} />}
      active={active}
      // Opens on first render when the current route is already inside this
      // section (goal spec); uncontrolled after that — matches Unleashed's
      // "stays open once you expand it" feel instead of fighting the user on
      // every navigation.
      defaultOpened={active}
      onClick={onNavigate}
      className="zs-nav-link"
      mb={2}
    >
      {section.children.map((child) => (
        <MantineNavLink
          key={child.to}
          component={Link}
          to={child.to}
          label={child.label}
          active={isNavActive(pathname, child.to)}
          onClick={onNavigate}
          className="zs-nav-link"
        />
      ))}
    </MantineNavLink>
  );
}

/** Active-state accent bar (design-uplift goal, leverage point 2): Mantine's own
 * `active` prop already tints the row's background/text via its `--nl-*` CSS
 * vars (primaryColor-driven), but gave no edge marker — every active row read
 * as "slightly greyer", easy to miss at a glance. `box-shadow: inset` (not an
 * absolutely-positioned pseudo-element) is deliberate: the navbar's ScrollArea
 * clips horizontal overflow, so a bar drawn OUTSIDE the link's own box would be
 * cut off; an inset shadow paints inside the element's own border box and is
 * immune to that. Scoped under `.zs-nav-link` (same "can't collide" prefix
 * convention as CenteredCard's `.zs-auth-*`), targeting NavLink's own
 * `data-active` attribute (verified against the installed package's NavLink.cjs
 * — `mod: [{ active }]` sets it) rather than a Mantine internal class name. */
const NAV_STYLES = `
.zs-nav-link[data-active] {
  box-shadow: inset 3px 0 0 0 var(--mantine-primary-color-filled);
  font-weight: 600;
}
`;

/** App shell (BUILD_SPEC.md §11): left nav + top bar, role-aware, with the tenant
 * switcher and global search box. Wraps every route nested under `_authenticated`
 * (src/routes/_authenticated.tsx) — never rendered for /login or /auth-callback.
 *
 * Navbar layout is Unleashed-parity chrome (Stuart ruling 2026-08-24: match the
 * navigation his team already knows from Unleashed Software) — brand block +
 * "Quick actions" pinned at top, the grouped/collapsible section tree scrolling
 * in the middle, Search/Help/Settings pinned at the bottom as a utility group.
 */
export function Shell({ children }: { children: ReactNode }) {
  const [navOpened, setNavOpened] = useState(false);
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const role = auth.activeTenant?.role ?? null;
  const features = useTenantFeatures();
  const sections = navSectionsForTenant(features).filter(
    (section) => !section.roles || (role && section.roles.includes(role)),
  );
  const utilityItems = UTILITY_ITEMS.filter((item) => !item.roles || (role && item.roles.includes(role)));
  // Reuses Home's own task list/roles (lib/taskButtons.ts) rather than forking a
  // second copy of "what can this role do" — Quick actions IS the Home task grid,
  // just reachable from every screen. Same `requiresSilos` gate Home applies
  // (routes/_authenticated/index.tsx) — otherwise a Howick user could still
  // reach "Silo reading" from here even with the nav section and Home card
  // both hidden.
  const tasks = HOME_TASKS.filter(
    (task) => (!role || task.roles.includes(role)) && (!task.requiresSilos || features.silosEnabled),
  );
  const closeNavOnMobile = () => setNavOpened(false);
  // Content-surface tint (design-uplift goal, leverage point 2): a flat literal
  // per scheme rather than Mantine's `--mantine-color-body` var, which resolves
  // to the SAME white/near-black that Card/Paper already sit on by default —
  // using it here would leave cards blended flush into the page again, the
  // opposite of the "cards sit ON a surface" goal. Computed in JS (not CSS
  // `light-dark()`) for the same reason CenteredCard avoids it: some gatehouse
  // tablets run older Android WebViews without support.
  const computedScheme = useComputedColorScheme('light');
  const surfaceTint = computedScheme === 'dark' ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-0)';

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: 280, breakpoint: 'sm', collapsed: { mobile: !navOpened } }}
      padding="md"
      styles={{ main: { backgroundColor: surfaceTint } }}
    >
      <style>{NAV_STYLES}</style>
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="md">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={navOpened} onClick={() => setNavOpened((o) => !o)} hiddenFrom="sm" size="sm" />
            {/* Compact mark+title (goal spec): the navbar's own brand block
                collapses off-screen on mobile (below the `sm` breakpoint), so
                brand needs to survive here too or it vanishes on a phone. */}
            <Group gap={6} wrap="nowrap">
              <img src="/brand/zebra-mark.png" alt="" width={22} height={22} style={{ borderRadius: 5 }} />
              <Text fw={700} size="lg" style={{ letterSpacing: '-0.01em' }}>
                Zebra Stock
              </Text>
            </Group>
          </Group>
          <GlobalSearch />
          <Group gap="xs" wrap="nowrap">
            <TenantSwitcher />
            <HelpButton />
            <ColorSchemeToggle />
            <UserMenu />
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm">
        <AppShell.Section>
          {/* Sidebar header carries TENANT context, not a second brand lockup
              (design-uplift goal, leverage point 2 — the topbar above already
              carries the brand; repeating it here was pure redundancy Stuart
              called out). "Tenant" eyebrow + the active tenant's name, so a
              multi-tenant user (Sabie vs Howick) always sees which one they're
              in without opening the switcher. */}
          <Stack gap={2} mb="md">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
              Tenant
            </Text>
            <Group gap={6} wrap="nowrap" justify="space-between">
              <Text fw={700} size="sm" truncate>
                {auth.activeTenant?.label ?? 'No tenant selected'}
              </Text>
              {role && (
                <Badge size="xs" variant="light" color="graphite" style={{ flexShrink: 0 }}>
                  {role}
                </Badge>
              )}
            </Group>
          </Stack>
          <Menu shadow="md" width={240} position="bottom-start">
            <Menu.Target>
              <Button variant="light" fullWidth leftSection={<IconPlus size={16} />}>
                Quick actions
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {tasks.length === 0 ? (
                <Menu.Item disabled>No quick actions for your role</Menu.Item>
              ) : (
                tasks.map((task) => (
                  <Menu.Item
                    key={task.label}
                    component={Link}
                    to={task.to}
                    leftSection={<task.icon size={16} stroke={1.5} />}
                    onClick={closeNavOnMobile}
                  >
                    {task.label}
                  </Menu.Item>
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        </AppShell.Section>

        <AppShell.Section grow component={ScrollArea} my="sm">
          {sections.map((section) => (
            <NavSectionLink key={section.to} section={section} pathname={pathname} onNavigate={closeNavOnMobile} />
          ))}
        </AppShell.Section>

        <AppShell.Section>
          <Divider label="More" labelPosition="left" mb="xs" c="dimmed" />
          {/* Reuses NavSectionLink (same component the main nav tree uses above)
              rather than a bespoke childless-only link — Settings now carries
              `children` (mirroring its own TABS, lib/nav.ts) and needs the same
              collapsible treatment; Search/Help stay childless and render
              exactly as before (NavSectionLink's own childless branch). */}
          {utilityItems.map((item) => (
            <NavSectionLink key={item.to} section={item} pathname={pathname} onNavigate={closeNavOnMobile} />
          ))}
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const q = value.trim();
    if (q) navigate({ to: '/search', search: { q } });
  }

  return (
    <form onSubmit={handleSubmit} style={{ flex: 1, maxWidth: 420 }}>
      <TextInput
        // Matches what the search module actually indexes (products, suppliers,
        // customers — services/api modules/search) — ticket indexing is a
        // roadmap item; promising it here was audit finding #4 (2026-07-07).
        placeholder="Search products, suppliers, customers…"
        leftSection={<IconSearch size={16} />}
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
      />
    </form>
  );
}

function TenantSwitcher() {
  const auth = useAuth();
  const navigate = useNavigate();
  if (!auth.activeTenant) return null;

  return (
    <Menu shadow="md" width={240} position="bottom-end">
      <Menu.Target>
        <UnstyledButton px="sm" py={6} style={{ borderRadius: 6 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={600}>
              {auth.activeTenant.label}
            </Text>
            <IconChevronDown size={14} />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Switch tenant</Menu.Label>
        {auth.tenants.map((tenant) => (
          <Menu.Item
            key={tenant.tenantId}
            fw={tenant.tenantId === auth.activeTenant?.tenantId ? 700 : 400}
            onClick={() => auth.switchTenant(tenant.tenantId)}
          >
            {tenant.label} · {tenant.role}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item onClick={() => navigate({ to: '/login' })}>Manage tenants…</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function ColorSchemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light');

  return (
    <ActionIcon
      variant="subtle"
      onClick={() => setColorScheme(computed === 'dark' ? 'light' : 'dark')}
      aria-label="Toggle color scheme"
    >
      {computed === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
    </ActionIcon>
  );
}

function UserMenu() {
  const auth = useAuth();
  const initial = (auth.userEmail ?? '?').charAt(0).toUpperCase();

  return (
    <Menu shadow="md" width={220} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" size="lg" aria-label="User menu" radius="xl">
          <Avatar radius="xl" size="sm" color="graphite">
            {initial}
          </Avatar>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{auth.userEmail ?? 'Signed in'}</Menu.Label>
        {auth.activeTenant && <Menu.Item disabled>Role: {auth.activeTenant.role}</Menu.Item>}
        <Menu.Divider />
        <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={() => auth.logout()}>
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
